import { Category } from "../types";

/**
 * ENG-3160 — what an org's locked policy pins on the member's Protected sites page.
 *
 * The extension applies a category-level lock to every site in the category (`api-client-service.ts`,
 * the ENG-2365 overlay). The page used to pin only sites named by slug and counted `enabledSites` as
 * the server sent them, so a locked category showed "(0/56 sites)" — the opposite of what the browser
 * was doing. These helpers are the page's single answer to "what does the org force here", so the
 * header count, the category box and each site row agree with each other and with the extension.
 *
 * Precedence is the extension's: locked > the member's own choice.
 *
 * ENG-4054: the site/category deny is retired. The backend sends `deniedCategories` / `deniedSites`
 * as `[]` and the extension enforces nothing from them, so they pin nothing here.
 */
export interface GoverningPolicy {
  lockedCategories?: string[];
  lockedSites?: string[];
}

export type PinKind = "locked" | null;

const has = (list: string[] | undefined, slug: string) => (list ?? []).includes(slug);

/** The org's pin on a category as a whole. */
export function categoryPin(policy: GoverningPolicy | null | undefined, categorySlug: string): PinKind {
  if (!policy) return null;
  if (has(policy.lockedCategories, categorySlug)) return "locked";
  return null;
}

/** The org's pin on one site: its own slug, or inherited from its category. */
export function sitePin(policy: GoverningPolicy | null | undefined, categorySlug: string, siteSlug: string): PinKind {
  if (!policy) return null;
  if (has(policy.lockedSites, siteSlug) || has(policy.lockedCategories, categorySlug)) return "locked";
  return null;
}

/** The state a pinned control shows: the org's choice wins, else the member's. */
export function pinnedChecked(pin: PinKind, enabled: boolean): boolean {
  return pin === "locked" ? true : enabled;
}

/** Sites in a category that are effectively on — pins included — for the "(n/m sites)" count. */
export function enabledCountFor(
  policy: GoverningPolicy | null | undefined,
  category: Pick<Category, "slug" | "sites">,
  enabledSites: Iterable<string>,
): number {
  const enabled = new Set(enabledSites);
  return category.sites.filter((s) => pinnedChecked(sitePin(policy, category.slug, s.slug), enabled.has(s.slug))).length;
}

/** One `/my-categories` answer. A field the server leaves out (it omits empty lists) reads as empty. */
export interface SelectionAnswer {
  categories: string[];
  enabledSites: string[];
  /** The per-site opt-outs (ENG-2342): off even though their category is on. */
  disabledSites: string[];
}

/** What the page shows as on, and what a save is built from. */
export interface ShownSelection {
  categories: string[];
  sites: string[];
}

/**
 * What the extension protects for a `/my-categories` read, which is what the page must show.
 *
 * An answer with no categories means Banking & Wealth to the extension (`api-client-service.ts`,
 * `fetchUserCategories`), with every site in it except the opt-outs. This helper used to default the
 * categories and not their sites (ENG-3160, ENG-3367). The page showed Banking & Wealth ticked with
 * "(0/56 sites)" while the extension protected all 56, and a site tick then saved that one site as
 * the org's whole banking protection. So the default covers categories and sites together, drawn
 * from the same catalog the page lists.
 *
 * Null when the answer needs the default and the catalog has not loaded: until then the page cannot
 * say which sites the default covers, and nothing may be saved from a guess. A backend with ENG-3367
 * answers with the default itself, so this covers one that predates it.
 */
export const DEFAULT_ENABLED_CATEGORIES = ["banking-wealth"];
export function shownSelection(
  answer: SelectionAnswer,
  catalog: Pick<Category, "slug" | "sites">[] | null,
): ShownSelection | null {
  if (answer.categories.length > 0) return { categories: answer.categories, sites: answer.enabledSites };
  if (!catalog) return null;
  const optedOut = new Set(answer.disabledSites);
  const sites = catalog
    .filter((c) => DEFAULT_ENABLED_CATEGORIES.includes(c.slug))
    .flatMap((c) => c.sites.map((s) => s.slug))
    .filter((slug) => !optedOut.has(slug));
  return { categories: DEFAULT_ENABLED_CATEGORIES, sites };
}
